import React, { useState, useEffect } from 'react';
import { useLogto } from '@logto/react';
import { Bot, X, Settings, Clock, HelpCircle, Loader2, Trash2 } from 'lucide-react';
import { modelService, ModelMetadata } from '../services/modelService';

interface SavedModelsBrowserProps {
  onSelectModel: (modelId: string, modelMetadata: ModelMetadata) => void;
  onClose: () => void;
}

export const SavedModelsBrowser: React.FC<SavedModelsBrowserProps> = ({
  onSelectModel,
  onClose,
}) => {
  const [models, setModels] = useState<ModelMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const logto = useLogto();

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const models = await modelService.listModels(logto);
      setModels(models);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading models');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (modelId: string) => {
    if (!window.confirm('Are you sure you want to delete this model?')) {
      return;
    }

    setDeletingId(modelId);
    try {
      const result = await modelService.deleteModel(logto, modelId);
      if (result.success) {
        setModels(models.filter((m) => m.id !== modelId));
      } else {
        setError(result.message || 'Failed to delete model');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting model');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMetricValue = (value: number | undefined): string => {
    if (value === undefined || value === null) return 'N/A';
    return typeof value === 'number' ? value.toFixed(4) : String(value);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="w-6 h-6" /> Saved Models
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-purple-800 rounded-full w-8 h-8 flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="mt-2 text-gray-600">Loading models...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-red-700">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {!loading && models.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <HelpCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p className="text-lg">No saved models yet</p>
              <p className="text-sm mt-2">Run an analysis to auto-save your trained models</p>
            </div>
          )}

          {!loading && models.length > 0 && (
            <div className="space-y-3">
              {models.map((model) => (
                <div
                  key={model.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-lg">{model.name}</h3>
                      {model.description && (
                        <p className="text-sm text-gray-600 mt-1">{model.description}</p>
                      )}

                      {/* Metrics Display */}
                      {model.metrics && (
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                          {model.metrics.nrmse_val !== undefined && (
                            <div className="bg-blue-50 p-2 rounded border border-blue-200">
                              <p className="text-gray-700 font-medium">NRMSE</p>
                              <p className="text-blue-900 font-bold">
                                {formatMetricValue(model.metrics.nrmse_val)}
                              </p>
                            </div>
                          )}
                          {model.metrics.r2 !== undefined && (
                            <div className="bg-green-50 p-2 rounded border border-green-200">
                              <p className="text-gray-700 font-medium">R²</p>
                              <p className="text-green-900 font-bold">
                                {formatMetricValue(model.metrics.r2)}
                              </p>
                            </div>
                          )}
                          {model.metrics.adj_r2 !== undefined && (
                            <div className="bg-indigo-50 p-2 rounded border border-indigo-200">
                              <p className="text-gray-700 font-medium">Adj. R²</p>
                              <p className="text-indigo-900 font-bold">
                                {formatMetricValue(model.metrics.adj_r2)}
                              </p>
                            </div>
                          )}
                          {model.metrics.decomp_rssd_val !== undefined && (
                            <div className="bg-red-50 p-2 rounded border border-red-200">
                              <p className="text-gray-700 font-medium">DECOMP.RSSD</p>
                              <p className="text-red-900 font-bold">
                                {formatMetricValue(model.metrics.decomp_rssd_val)}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="flex gap-4 mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Settings className="w-3 h-3" /> Config: {model.config_name}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(model.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => onSelectModel(model.id, model)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      >
                        Load
                      </button>
                      <button
                        onClick={() => handleDelete(model.id)}
                        disabled={deletingId === model.id}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        {deletingId === model.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
